export const isString = (str: unknown): str is string => typeof str === 'string'

// These return a non-string input untouched, hence the generic rather than a `string` parameter:
// callers that pass a string still get a string back, and the rest keep their own type.
export const replaceAllSpaces = <T>(str: T): T | string => (isString(str) ? str.replaceAll(' ', '') : str)
export const replaceAllSpacesAndAmpersand = <T>(str: T): T | string => (isString(str) ? str.replace(/ |& /g, '') : str)
export const getOnlyDigits = <T>(str: T): T | string => (isString(str) ? str.replace(/\D/g, '') : str)

// Will automatically handle the cases like ACH b/c of + pattern
export const insertSpaceBeforeCapitalLetter = <T>(str: T): T | string => (isString(str) ? str.replace(/([A-Z]+)/g, ' $1').trim() : str)

export const parseLHSbracketStr = (str: string): { key: string; operation: string | null } => {
  if (!str.includes('[') && !str.includes(']')) return { key: str, operation: null }
  const key = str.substring(0, str.indexOf('['))
  const operation = str.substring(str.indexOf('[') + 1, str.lastIndexOf(']'))
  return { key, operation }
}

export const extractNameInitials = (name?: string | null): string => {
  if (!name) return ''
  const regex = /[^\w\s]|_/g
  const initials = name.replace(regex, '').match(/\b\w/g)
  if (initials === null || initials.length < 1) {
    return ''
  }
  return initials[0].toUpperCase() + (initials.length > 1 ? initials[1].toUpperCase() : '')
}

export const generateRandomId = (length: number): string => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const values = new Uint32Array(length)
  window.crypto.getRandomValues(values)
  let result = ''
  for (let i = 0; i < length; i++) {
    result += charset[values[i] % charset.length]
  }
  return result
}

export const removePrefix = (str: string, prefix: string): string => {
  if (str.startsWith(prefix)) {
    return str.substring(prefix.length)
  }
  return str
}

/**
 * Enum-shaped API values to display casing: 'READY_FOR_INVOICE' -> 'Ready For Invoice'. Splits on
 * underscores, hyphens and spaces only — it does not break camelCase the way lodash startCase does.
 */
export const titleCaseFromEnum = <T>(value: T): T | string => {
  if (!isString(value)) return value
  return value
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Normalize casing from API format (e.g., 'DAY' -> 'Day', 'MONTH' -> 'Month')
export const normalizeCasing = <T>(value: T): T | string =>
  isString(value) && value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value
